import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate, useParams } from "react-router-dom";

import { Breadcrumb } from "@/components/Breadcrump/Breadcrump";

import { Button } from "@/components/Button/Button";

import { LoadingOverlay } from "@/components";

import {
	useGetTrackerQuery,
	useLazyDownloadTrackerFileQuery,
} from "@/pages/Private/redux/profileTracker/profileTracker.api";

import { TrackerSourceType } from "@/enum/tracker.enum";

import { getListBuilderTitle } from "@/pages/Private/pages/List/utils/listBuilderTitle";

import { getAvailableSteps, getDoneSteps } from "../../utils/create-tracker-steps";

import { Stepper } from "./components/Stepper";

import { Tracker, TrackerFile } from "../../schema/profileTracker";
import { CreateXlsxForm } from "../../components/CreateXlsxForm";
import { AddedSourceTracker } from "../../components/AddedSourceList";

export const TrackerEdit: FunctionComponent = () => {
	const { t } = useTranslation();
	const { id = 0 } = useParams();

	const navigate = useNavigate();
	const ts = (key: string) => t(`profileTracker.${key}`);

	const { data, isFetching } = useGetTrackerQuery(+id);

	const [selected, setSelected] = useState<TrackerFile | null>(null);

	const [downloadTrackerFile, { isLoading: downloadIsLoading }] = useLazyDownloadTrackerFileQuery();

	const handleDownload = async (trackerFile: TrackerFile) => {
		const blob = await downloadTrackerFile(trackerFile.id).unwrap();

		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");

		a.href = url;
		a.download = `${trackerFile.name}.xlsx`;
		document.body.appendChild(a);
		a.click();
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
	};

	const amountOfSources = useMemo(() => {
		if (!data) {
			return 0;
		}
	}, [data]);

	const submit = async () => {
		navigate(`/app/profile-tracker/settings/${id}`);
	};

	return isFetching || !data ? (
		<LoadingOverlay />
	) : (
		<div className="sm:block flex flex-col items-start justify-between mb-6 pb-8">
			<Breadcrumb
				path={[
					{ appFunction: "profile-tracker", name: ts("title"), link: "/" },
					{ appFunction: "profile-tracker", name: ts("create.title"), link: "/create" },
					{ appFunction: "profile-tracker", name: data?.name || "", link: `/create/${id}/details` },
				]}
			/>
			<div className="text-lg leading-9 mb-5">{getListBuilderTitle(ts(data.sourceType))}</div>
			<Stepper
				availableSteps={getAvailableSteps(data)}
				currentStep="source"
				doneSteps={getDoneSteps(data)}
				listId={data.id}
			/>
			<div className="w-full">
				{data.sourceType === TrackerSourceType.XLSX && (
					<div className="flex flex-row gap-12">
						<div className="grow relative">
							{selected ? (
								<CreateXlsxForm
									handleSelect={setSelected}
									tracker={data as Tracker}
									trackerFile={selected as TrackerFile}
								/>
							) : (
								<CreateXlsxForm tracker={data as Tracker} />
							)}
						</div>
						<div className="w-1/2">
							<AddedSourceTracker
								downloadIsLoading={downloadIsLoading}
								handleDownload={handleDownload}
								tracker={data as Tracker}
							/>
						</div>
					</div>
				)}
			</div>
			<div className="absolute mt-14 h-16 flex flex-row justify-between items-center w-auto border-t border-t-border px-8 py-[14px] rounded-t-16 bottom-0 right-0 z-[999] bg-white w-full">
				<div></div>
				<div className="flex flex-row self-left items-center gap-x-4">
					<Button disabled={amountOfSources === 0} title={t("basics.next")} onClick={submit} />
				</div>
			</div>
		</div>
	);
};
